/**
 * Teacher Home header (docs/06-UI-SPEC.md §5): logo left, Lecture Record and the
 * profile menu right. Sign Out lives in the profile menu, per docs/02-SRS.md B2.
 */

import { useEffect, useState } from 'react';

import { Logo } from '../assets/Logo.js';

export interface HomeHeaderProps {
  readonly email: string;
  readonly onOpenLectureRecord: () => void;
  readonly onSignOut: () => void;
}

interface ProfileMenuProps {
  readonly email: string;
  readonly onSignOut: () => void;
}

interface MenuPanelProps extends ProfileMenuProps {
  readonly onDismiss: () => void;
}

function MenuPanel({ email, onSignOut, onDismiss }: MenuPanelProps) {
  return (
    <>
      {/* Catches the click that closes the menu without stealing it from the page. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="fixed inset-0 z-40 cursor-default"
        onClick={onDismiss}
      />
      <div
        role="menu"
        className="absolute right-0 z-50 mt-2 flex w-[240px] flex-col gap-1 rounded-md border border-border bg-surface p-2 shadow-md"
      >
        <span className="truncate px-2 py-1 text-sm text-text-secondary">{email}</span>
        <button
          type="button"
          role="menuitem"
          onClick={onSignOut}
          className="h-touch rounded-md px-2 text-left text-base text-text-primary transition duration-fast ease-out hover:bg-surface-hover"
        >
          Sign out
        </button>
      </div>
    </>
  );
}

function ProfileMenu({ email, onSignOut }: ProfileMenuProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Your account"
        onClick={() => {
          setOpen((wasOpen) => !wasOpen);
        }}
        className="flex h-touch w-touch items-center justify-center rounded-full bg-primary-subtle text-base font-semibold uppercase text-primary transition duration-fast ease-out hover:bg-surface-hover"
      >
        {email.slice(0, 1)}
      </button>
      {open ? (
        <MenuPanel
          email={email}
          onSignOut={onSignOut}
          onDismiss={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

export function HomeHeader({ email, onOpenLectureRecord, onSignOut }: HomeHeaderProps) {
  return (
    <header className="flex h-header items-center justify-between border-b border-border bg-surface px-6">
      <Logo size={32} />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onOpenLectureRecord}
          className="h-touch rounded-md border border-border bg-surface px-4 text-base font-medium text-text-primary transition duration-fast ease-out hover:bg-surface-hover"
        >
          Lecture Record
        </button>
        <ProfileMenu email={email} onSignOut={onSignOut} />
      </div>
    </header>
  );
}
